import {
	Card,
	Checkbox,
	Dropdown,
	Flex,
	Modal,
	Table,
	Tooltip,
	Typography,
	Empty,
	message,
} from 'antd';
import { useState } from 'react';
import { useDeleteMaterialReservationMutation } from '../../../redux/api/material';
import { Lineitem } from '../../../interfaces/Ordel.Interface';
import { LoadingOutlined } from '@ant-design/icons';
import {
	useGetGroupedRelatedMaterialReservationsQuery,
	useGetStockPreviewByDimensionsQuery,
} from '../../../redux/api/order-line-item';
import {
	GroupedMaterialReservation,
	MaterialReservation,
} from '../../../interfaces/MaterialInventory.interface';
import ThreeDStockSizePreviewer from '../../../components/ThreeDStockPreviewer/lazy.tsx';
import { MaterialReservationStatus } from '../../../components/MaterialReservationStatus';
import { useHasMultipleBuildings } from '../../../hooks/useHasMultipleBuildings';
import {
	hasRecordedCuts,
	releaseErrorMessage,
	releaseTooltip,
} from '../../../utils/materialReservationRelease';

const { Title, Text } = Typography;

export const MaterialReservations = ({
	lineItem,
	refetchLineItem,
}: {
	lineItem: Lineitem;
	refetchLineItem: () => void;
}) => {
	const [isStockPreviewModalOpen, setIsStockPreviewModalOpen] = useState(false);
	const [selectedMaterialReservation, setSelectedMaterialReservation] =
		useState<MaterialReservation>();

	const [groupByMaterial, setGroupByMaterial] = useState<boolean>(false);
	const hasMultipleBuildings = useHasMultipleBuildings();

	const [deleteMaterialReservation] = useDeleteMaterialReservationMutation();

	const groupedMaterialReservations =
		useGetGroupedRelatedMaterialReservationsQuery(
			{
				id: lineItem.id,
			},
			{ skip: !lineItem.id, refetchOnMountOrArgChange: true }
		);

	const stockPreviewQuery = useGetStockPreviewByDimensionsQuery(
		{
			id: lineItem.id,
			params: {
				shape:
					selectedMaterialReservation?.material_length?.material?.shape?.id,
				height: selectedMaterialReservation?.material_length?.material?.height,
				width: selectedMaterialReservation?.material_length?.material?.width,
				outer_diameter:
					selectedMaterialReservation?.material_length?.material
						?.outer_diameter,
				inner_diameter:
					selectedMaterialReservation?.material_length?.material
						?.inner_diameter,
				length: selectedMaterialReservation?.material_cut_length,
				stock_facing_amount: lineItem?.stock_facing_amount || 0.03,
			},
		},
		{
			skip: !selectedMaterialReservation && !isStockPreviewModalOpen,
			refetchOnMountOrArgChange: true,
		}
	);

	async function handleDeleteMaterialReservation(id: string) {
		// Pass the line item so the cached copy (and with it the scrap allowance
		// the reservation modal pre-fills from) is refreshed. PTB-5965.
		await deleteMaterialReservation({ id, orderLineItemId: lineItem.id })
			.unwrap()
			.then(() => refetchLineItem())
			.catch((error) => message.error(releaseErrorMessage(error)));
	}

	const EmptyTable = () => {
		return <Empty description="No material reservations" />;
	};

	return (
		<>
			<Flex justify="space-between" align="center">
				<>
					<div>
						<Title level={5}>Material Reservations</Title>
					</div>
					<div className="mb-3">
						<Text>
							{`${lineItem?.total_reserved_material_length} total inches, ${lineItem.reserved_material_lengths_count} bars, ${lineItem.total_reserved_material_cut_quantity} pieces of stock`}
						</Text>
					</div>
				</>
			</Flex>

			<div>
				<Checkbox
					checked={groupByMaterial}
					onChange={(e) => setGroupByMaterial(e.target.checked)}
				>
					Group By Material
				</Checkbox>
			</div>

			{groupByMaterial ? (
				<Table
					dataSource={groupedMaterialReservations?.data?.map(
						(item: GroupedMaterialReservation, index: number) => {
							return {
								key: index,
								...item,
							};
						}
					)}
					size="small"
					columns={[
						{
							key: 'material',
							render: (record) => (
								<Text>
									<a href={`/material/materials/?search=${record.id}`}>
										{`${record.material.display_name}`}
									</a>
								</Text>
							),
						},
					]}
					rowKey="key"
					locale={{ emptyText: <EmptyTable /> }}
					pagination={{ pageSize: 5 }}
					expandable={{
						expandedRowRender: (material: GroupedMaterialReservation) => (
							<Card size="small">
								<Table
									size="small"
									dataSource={material.reservations}
									pagination={false}
								columns={[
									{
										title: 'Cut Qty.',
										key: 'Cut Length',
										dataIndex: 'material_cut_quantity',
										render: (value) => value,
									},
									...(hasMultipleBuildings ? [{
										title: 'Building',
										key: 'building',
										render: (_: unknown, reservation: MaterialReservation) => {
											const building = (reservation?.material_length?.inventory_location as any)?.shelf?.rack?.building;
											return <Text>{building?.name || '—'}</Text>;
										},
									}] : []),
									{
										title: 'Cut Length',
										key: 'Cut Length',
										dataIndex: 'material_cut_length',
										render: (record) => `${record} in.`,
									},
									{
										title: 'Reserved Length',
										key: 'Reserved Length',
										dataIndex: 'reserved_length',
										render: (record) => `${record} in.`,
									},
									{
										title: 'Cut Status',
										key: 'status',
										render: (reservation) => (
											<MaterialReservationStatus
												reservation={reservation}
												refetch={refetchLineItem}
											/>
										),
									},
										{
											title: 'Actions',
											key: 'actions',
											render: (text, reservation) => (
												<Dropdown.Button
													onClick={() => {
														setSelectedMaterialReservation(reservation);
														setIsStockPreviewModalOpen(true);
													}}
													menu={{
														items: [
															{
																key: '1',
																label: (
																	<Tooltip
																		title={releaseTooltip(reservation)}
																	>
																		Release
																	</Tooltip>
																),
																disabled: hasRecordedCuts(reservation),
																onClick: () =>
																	handleDeleteMaterialReservation(
																		reservation.id
																	),
															},
															// {
															// 	key: '2',
															// 	label: (
															// 		<Tooltip title="Set the material length status as Cut and remove the reserved length from the inventory">
															// 			Mark as cut
															// 		</Tooltip>
															// 	),
															// 	onClick: () =>
															// 		confirm({
															// 			icon: null,
															// 			centered: true,
															// 			title: 'Mark material reservation as cut?',
															// 			content: (
															// 				<>
															// 					This will subtract the reserved length
															// 					from the current length in the database.
															// 				</>
															// 			),
															// 			okButtonProps: {},
															// 			onOk() {
															// 				handleMarkMaterialReservationAsCut(
															// 					reservation.id
															// 				);
															// 			},
															// 		}),
															// },
															{
																key: '3',
																label: (
																	<Tooltip title="See a 3D preview of the stock and part">
																		Preview Stock
																	</Tooltip>
																),
																onClick: () => {
																	setSelectedMaterialReservation(reservation);
																	setIsStockPreviewModalOpen(true);
																},
															},
														],
													}}
													trigger={['click']}
													placement="bottomRight"
												>
													Preview
												</Dropdown.Button>
											),
										},
									]}
								/>
							</Card>
						),
					}}
				/>
			) : (
				<Table
					dataSource={lineItem.material_reservations}
				columns={[
					{
						title: 'Material',
						dataIndex: 'material',
						key: 'material',
						render: (text, reservation) => (
							<Text>
								<a
									href={`/material/materials/?search=${reservation?.material_length?.material?.id}`}
								>
									{`${reservation?.material_length?.material?.display_name}`}
								</a>
							</Text>
						),
					},
					...(hasMultipleBuildings ? [{
						title: 'Building',
						key: 'building',
						render: (_: unknown, reservation: MaterialReservation) => {
							const building = (reservation?.material_length?.inventory_location as any)?.shelf?.rack?.building;
							return <Text>{building?.name || '—'}</Text>;
						},
					}] : []),
					{
						title: 'Cut Qty.',
						key: 'Cut Length',
						dataIndex: 'material_cut_quantity',
						render: (value) => value,
					},
					{
						title: 'Cut Length',
						key: 'Cut Length',
						dataIndex: 'material_cut_length',
						render: (record) => `${record} in.`,
					},
					{
						title: 'Reserved Length',
						key: 'Reserved Length',
						dataIndex: 'reserved_length',
						render: (record) => `${record} in.`,
					},
					{
						title: 'Cut Status',
						key: 'status',
						render: (reservation) => (
							<MaterialReservationStatus
								reservation={reservation}
								refetch={() => {
									refetchLineItem();
								}}
							/>
						),
					},
					{
						title: 'Actual Cut Qty',
						key: 'actual_cut_quantity',
						render: (record) => (
							<>
								{record.actual_cut_quantity} of {record.material_cut_quantity}
							</>
						),
					},
					{
						title: 'Created By',
						key: 'created_by',
						render: (_, reservation) => (
							<Text>{reservation.created_by || '—'}</Text>
						),
					},
						{
							title: 'Actions',
							key: 'actions',
							render: (text, reservation) => (
								<Dropdown.Button
									onClick={() => {
										setSelectedMaterialReservation(reservation);
										setIsStockPreviewModalOpen(true);
									}}
									menu={{
										items: [
											{
												key: '1',
												label: (
													<Tooltip title={releaseTooltip(reservation)}>
														Release
													</Tooltip>
												),
												disabled: hasRecordedCuts(reservation),
												onClick: () =>
													handleDeleteMaterialReservation(reservation.id),
											},
											// {
											// 	key: '2',
											// 	label: (
											// 		<Tooltip title="Set the material length status as Cut and remove the reserved length from the inventory">
											// 			Mark as cut
											// 		</Tooltip>
											// 	),
											// 	onClick: () =>
											// 		confirm({
											// 			icon: null,
											// 			centered: true,
											// 			title: 'Mark material reservation as cut?',
											// 			content: (
											// 				<>
											// 					This will subtract the reserved length from the
											// 					current length in the database.
											// 				</>
											// 			),
											// 			okButtonProps: {},
											// 			onOk() {
											// 				handleMarkMaterialReservationAsCut(
											// 					reservation.id
											// 				);
											// 			},
											// 		}),
											// },
										],
									}}
									trigger={['click']}
									placement="bottomRight"
								>
									Preview
								</Dropdown.Button>
							),
						},
					]}
					rowKey={(reservation) => reservation.id}
					size="small"
					pagination={{ pageSize: 5 }}
					locale={{ emptyText: <EmptyTable /> }}
				/>
			)}

			<Modal
				open={isStockPreviewModalOpen}
				width={'960px'}
				title={null}
				centered={true}
				footer={false}
				styles={{ body: { padding: 0 } }}
				onCancel={() => setIsStockPreviewModalOpen(false)}
			>
				<div style={{ height: '720px', overflow: 'hidden' }}>
					{stockPreviewQuery.isLoading ? (
						<LoadingOutlined />
					) : (
					<ThreeDStockSizePreviewer
						partBufferGeometry={stockPreviewQuery?.data?.part}
						stockBufferGeometry={stockPreviewQuery?.data?.stock}
						jawsBufferGeometry={
							lineItem.partsbadger_production_method === 'Swiss'
								? undefined
								: stockPreviewQuery?.data?.jaws
						}
						partMatrix={lineItem.part_transform_matrix}
						showPartTransformControls={false}
						loading={stockPreviewQuery?.isLoading}
					/>
					)}
				</div>
			</Modal>
		</>
	);
};
